import React from 'react';
import { useNavigate } from 'react-router-dom';

import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import { styled } from '@mui/material/styles';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import InfiniteScroll from 'react-infinite-scroller';

import { black, primarycolor } from '../../constants/Colors';
import { purpose } from '../../constants/Select';
import { select } from '../../constants/enum';

import GivenListCard from './GivenListCard';
import NothingGivenList from './NothingGivenList';

import type { Issue } from '../../api/firebase/firestore/issue/issueType';

const styles = {
  container: {
    '@media screen and (max-width:600px)': {
      padding: '0 8px'
    },
    padding: '0 35px'
  },
  givenListSize: {
    '@media screen and (max-width:480px)': { margin: '0 16px 16px 16px' },
    border: '1px solid #DBDBE0',
    borderRadius: '0px',
    boxShadow: '0',
    margin: '0px 24px 24px 24px'
  },
  gridItem: {
    '@media screen and (max-width:600px)': {
      padding: '0'
    },
    marginBottom: '26px'
  },
  header: {
    '@media screen and (max-width:600px)': {
      margin: '31px 16px 25px'
    },
    alignItems: 'center',
    display: 'flex',
    justifyContent: 'space-between',
    margin: '44px 40px 24px'
  },
  headerTextStyle: {
    '@media screen and (max-width:600px)': {
      fontSize: '18px'
    },
    color: black,
    fontSize: '24px',
    fontWeight: '700'
  }
};

const BootstrapButton = styled(Button)({
  '&:active': {
    backgroundColor: primarycolor,
    borderColor: primarycolor,
    boxShadow: 'none'
  },

  '&:focus': {
    boxShadow: '0 0 0 0.2rem rgb(0 123 255 / 50%)'
  },

  '&:hover': {
    backgroundColor: primarycolor,
    borderColor: primarycolor,
    boxShadow: 'none'
  },
  '@media screen and (max-width:600px)': {
    fontSize: '13px',
    padding: '4px 10px'
  },
  backgroundColor: primarycolor,
  border: '1px solid',
  borderColor: primarycolor,
  boxShadow: 'none',
  fontSize: '14px',

  lineHeight: 1.5,

  padding: '6px 16px',

  textTransform: 'none'
});

interface IssueProps {
  issueList?: Issue[];
  screen: number;
  loadMore?: () => Promise<void>;
  hasMore: boolean;
}

const RegisterInfoGivenListSize: React.FC<IssueProps> = (props) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { issueList, screen, loadMore, hasMore } = props;

  const purposeList = purpose();
  const getPurpose = (value: number | null | undefined): string | undefined => {
    const purposeData = purposeList?.find((element: { value: number }) =>
      element.value === value ? element : null
    );
    return purposeData?.option;
  };

  const justifyContent = (): object => {
    if (issueList === undefined || issueList.length === 0) {
      return { justifyContent: 'center' };
    }
    return { justifyContent: 'left' };
  };

  return (
    <>
      <Card sx={styles.givenListSize}>
        <Typography sx={styles.header} component="div">
          <Typography sx={styles.headerTextStyle}>
            {t('given.givenlist')}
          </Typography>
          <BootstrapButton
            variant="contained"
            disableRipple
            onClick={() => {
              navigate('/client/mypage/issue/edit?type=add');
            }}
          >
            ＋ {t('given.register')}
          </BootstrapButton>
        </Typography>

        <Typography sx={styles.container} component="div">
          <InfiniteScroll
            loadMore={() => {
              void (async () => {
                loadMore && (await loadMore());
              })();
            }}
            hasMore={hasMore}
            loader={<div className="loader" key={0}></div>}
          >
            <Grid container sx={[{ width: 'auto' }, justifyContent()]}>
              {issueList
                ? issueList.map((item, index) => (
                    <Grid item xs={12} sm={4} key={index}>
                      <Typography component="div" sx={styles.gridItem}>
                        <GivenListCard
                          screen={screen}
                          image={item.imageId ? item.imageId[0] : ''}
                          clientId={item.companyId}
                          name={item.productName ? item.productName : ''}
                          title={item.name}
                          season={
                            item.startDate
                              ? dayjs(item.startDate?.toDate()).format(
                                  'YYYY/MM/DD'
                                )
                              : t('issue.consultation')
                          }
                          deadline={`${
                            item.applicationDate?.start
                              ? dayjs(
                                  item.applicationDate?.start?.toDate()
                                ).format('YYYY/MM/DD')
                              : ''
                          }${
                            item.applicationDate?.start &&
                            item.applicationDate?.end
                              ? '〜'
                              : ''
                          }${
                            item.applicationDate?.end
                              ? dayjs(
                                  item.applicationDate?.end?.toDate()
                                ).format('YYYY/MM/DD')
                              : ''
                          }`}
                          purpose={
                            item?.purpose && item?.purpose[0] !== select.other
                              ? getPurpose(item?.purpose[0])
                              : item?.purposeOther
                          }
                          issueId={item.issueId}
                        />
                      </Typography>
                    </Grid>
                  ))
                : ''}
              {issueList === undefined ||
                (issueList.length === 0 && (
                  <Grid item>
                    <NothingGivenList
                      title={t('list.title')}
                      detail={t('list.detail')}
                    />
                  </Grid>
                ))}
            </Grid>
          </InfiniteScroll>
        </Typography>
      </Card>
    </>
  );
};

export default RegisterInfoGivenListSize;
